/** Shared RTK Query cache and live-update bridge for project tasks. */
/* eslint-disable max-lines-per-function, max-statements */
import { eventService } from '../../services/eventService';
import { readPreferredModel } from '../../services/modelCatalog';
import type {
  Task,
  TaskStatus,
  TaskVersion,
  TaskVersionDetail,
} from '../../services/taskService';

import { api } from './api';
import { applyTaskStatusUpdate, mergeTaskBatch, type TaskStatusUpdate } from './taskCache';

export interface TaskIdentity {
  projectId: string;
  taskId: string;
}

interface CreateTaskArgs {
  projectId: string;
  task: Partial<Task>;
}

export interface GenerateTasksWithAiArgs {
  projectId: string;
  prompt: string;
  sessionId: string | null;
  requestId: string;
}

export interface GenerateTasksWithAiResponse {
  generation_id: string;
  status: 'accepted';
  message_id: string;
  overview_entry_id: string;
}

interface UpdateTaskArgs extends TaskIdentity {
  task: Partial<Task>;
}

interface ReorderTaskArgs extends TaskIdentity {
  position: number;
  status?: TaskStatus;
}

interface BatchStatusResult {
  message: string;
  /** Requested tasks the server did not change. */
  skipped_task_ids?: string[];
}

interface BatchStatusArgs {
  projectId: string;
  taskIds: string[];
  status: TaskStatus;
}

interface BatchReorderArgs {
  projectId: string;
  updates: { id: string; position: number }[];
}

interface BatchDeleteArgs {
  projectId: string;
  taskIds: string[];
}

interface ResumeTaskResponse {
  success?: boolean;
  message?: string;
}

interface TaskVersionIdentity {
  projectId: string;
  versionNumber: number;
}

const replaceTasks = (draft: Task[], tasks: Task[]) => {
  draft.splice(0, draft.length, ...tasks);
};

const replaceTask = (draft: Task[], task: Task) => {
  const index = draft.findIndex((candidate) => candidate.id === task.id);

  if (index >= 0) draft[index] = task;
  else draft.push(task);
};

const taskTags = (result: Task[] | undefined, projectId: string) => [
  { type: 'Task' as const, id: projectId },
  ...(result ?? []).map((task) => ({
    type: 'Task' as const,
    id: `${projectId}:${task.id}`,
  })),
];

const eventIsForProject = (projectId: string): boolean => (
  eventService.isCurrentProject(projectId)
);

const wireTaskCacheEvents = (
  projectId: string,
  updateCachedData: (recipe: (draft: Task[]) => void) => unknown,
  invalidate: () => void,
  ignoreInitialConnected: boolean,
) => {
  let refreshTimer: ReturnType<typeof setTimeout> | undefined;
  let shouldIgnoreConnected = ignoreInitialConnected;
  const scheduleRefresh = (delay = 0) => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(invalidate, delay);
  };
  const unsubscribeTasks = eventService.subscribe('tasks_update', (data: unknown) => {
    if (!eventIsForProject(projectId) || !Array.isArray(data)) return;
    updateCachedData((draft) => {
      const merged = mergeTaskBatch(draft, data as Task[]);

      if (merged !== draft) replaceTasks(draft, merged);
    });
  });
  const unsubscribeTask = eventService.subscribe('task_update', (data: unknown) => {
    if (!eventIsForProject(projectId)) return;
    let found = false;
    let needsSnapshotRefresh = false;

    updateCachedData((draft) => {
      const result = applyTaskStatusUpdate(draft, data as TaskStatusUpdate);

      found = result.found;
      needsSnapshotRefresh = result.needsSnapshotRefresh;
      if (found && result.tasks !== draft) replaceTasks(draft, result.tasks);
    });
    // Completion persists artifact receipts and workspace deployment evidence,
    // but the compact task_update carries only status/progress. Refresh the full
    // task so output actions and the Stage readiness indicator update together.
    if (!found || needsSnapshotRefresh) scheduleRefresh();
  });
  // Planning-task charges are persisted alongside the balance update. Refetch
  // so "Credits needed" changes to "Credits used" even when the accompanying
  // task_update only carries status/progress fields.
  const unsubscribeCredits = eventService.subscribe('credits_update', () => {
    if (!eventIsForProject(projectId)) return;
    scheduleRefresh();
  });
  // Changed requirements send the model a different request, which lifts a declined task's Retry block.
  const unsubscribeRequirements = eventService.subscribe('requirements', () => {
    if (!eventIsForProject(projectId)) return;
    let blocked = false;

    updateCachedData((draft) => {
      blocked = draft.some((task) => task.failure_notice?.retry_blocked);
    });
    if (blocked) scheduleRefresh(1000);
  });
  const unsubscribeBuild = eventService.subscribe('build_update', (data: unknown) => {
    if (!eventIsForProject(projectId)) return;
    const status = (data as { status?: string }).status;

    if (status === 'IN_PROGRESS') scheduleRefresh();
    else if (status === 'WARMING_UP') scheduleRefresh(800);
  });
  const unsubscribePlanningComplete = eventService.subscribe('planning_complete', (data: unknown) => {
    const payload = data as { project_id?: string } | null;

    if (!eventIsForProject(projectId) || (payload?.project_id && payload.project_id !== projectId)) return;
    scheduleRefresh();
  });
  const unsubscribeConnected = eventService.subscribe('connected', () => {
    if (!eventIsForProject(projectId)) return;
    if (shouldIgnoreConnected) {
      shouldIgnoreConnected = false;

      return;
    }
    scheduleRefresh();
  });
  // Manual refresh is also the recovery path when the live stream is down.
  const onManualRefresh = () => scheduleRefresh();

  window.addEventListener('refresh_tasks', onManualRefresh);

  return () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    unsubscribeTasks();
    unsubscribeTask();
    unsubscribeCredits();
    unsubscribeRequirements();
    unsubscribeBuild();
    unsubscribeConnected();
    unsubscribePlanningComplete();
    window.removeEventListener('refresh_tasks', onManualRefresh);
  };
};

export const tasksApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<Task[], string>({
      query: (projectId) => `/api/v1/projects/${projectId}/tasks`,
      keepUnusedDataFor: 60,
      providesTags: (result, _error, projectId) => taskTags(result, projectId),
      async onCacheEntryAdded(
        projectId,
        { cacheDataLoaded, cacheEntryRemoved, dispatch, updateCachedData },
      ) {
        const ignoreInitialConnected = !eventService.isOpenTo(projectId);

        eventService.connect(projectId);
        let initialLoadComplete = false;
        let refreshPending = false;
        const invalidate = () => {
          if (!initialLoadComplete) {
            refreshPending = true;

            return;
          }
          dispatch(api.util.invalidateTags([{ type: 'Task', id: projectId }]));
        };
        const cleanup = wireTaskCacheEvents(
          projectId,
          updateCachedData,
          invalidate,
          ignoreInitialConnected,
        );

        try {
          try {
            await cacheDataLoaded;
          } catch {
            // A failed initial query is exposed by RTK Query to its consumers.
          } finally {
            initialLoadComplete = true;
            if (refreshPending) invalidate();
          }
          await cacheEntryRemoved;
        } finally {
          cleanup();
        }
      },
    }),
    getTask: builder.query<Task, TaskIdentity>({
      query: ({ projectId, taskId }) => `/api/v1/projects/${projectId}/tasks/${taskId}`,
      providesTags: (_result, _error, { projectId, taskId }) => [
        { type: 'Task' as const, id: `${projectId}:${taskId}` },
      ],
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
            replaceTask(draft, data);
          }));
        } catch {
          // The task query exposes its own error state.
        }
      },
    }),
    createTask: builder.mutation<Task, CreateTaskArgs>({
      query: ({ projectId, task }) => ({
        url: `/api/v1/projects/${projectId}/tasks`,
        method: 'POST',
        body: task,
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
            replaceTask(draft, data);
          }));
        } catch {
          // The caller owns the mutation error.
        }
      },
    }),
    generateTasksWithAi: builder.mutation<GenerateTasksWithAiResponse, GenerateTasksWithAiArgs>({
      query: ({ projectId, prompt, sessionId, requestId }) => ({
        url: `/api/v1/projects/${projectId}/tasks/ai-generate`,
        method: 'POST',
        body: {
          prompt,
          session_id: sessionId,
          request_id: requestId,
        },
      }),
    }),
    updateTask: builder.mutation<Task, UpdateTaskArgs>({
      query: ({ projectId, taskId, task }) => ({
        url: `/api/v1/projects/${projectId}/tasks/${taskId}`,
        method: 'PUT',
        body: task,
      }),
      async onQueryStarted({ projectId, taskId, task }, { dispatch, queryFulfilled }) {
        const listPatch = dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
          const index = draft.findIndex((candidate) => candidate.id === taskId);

          if (index >= 0) draft[index] = { ...draft[index], ...task };
        }));
        const detailPatch = dispatch(tasksApi.util.updateQueryData(
          'getTask',
          { projectId, taskId },
          (draft) => { Object.assign(draft, task); },
        ));

        try {
          const { data } = await queryFulfilled;

          dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
            replaceTask(draft, data);
          }));
          dispatch(tasksApi.util.upsertQueryData('getTask', { projectId, taskId }, data));
        } catch {
          listPatch.undo();
          detailPatch.undo();
        }
      },
    }),
    pauseTask: builder.mutation<Task, TaskIdentity>({
      query: ({ projectId, taskId }) => ({
        url: `/api/v1/projects/${projectId}/tasks/${taskId}/pause`,
        method: 'POST',
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
            replaceTask(draft, data);
          }));
          dispatch(tasksApi.util.upsertQueryData(
            'getTask',
            { projectId, taskId: data.id },
            data,
          ));
        } catch {
          // The caller owns the mutation error.
        }
      },
    }),
    resumeTask: builder.mutation<ResumeTaskResponse, TaskIdentity>({
      query: ({ projectId, taskId }) => ({
        url: `/api/v1/projects/${projectId}/tasks/${taskId}/resume`,
        method: 'POST',
        body: { model_id: readPreferredModel() },
      }),
      async onQueryStarted({ projectId, taskId }, { dispatch, queryFulfilled }) {
        const listPatch = dispatch(tasksApi.util.updateQueryData(
          'getTasks',
          projectId,
          (draft) => {
            const task = draft.find((candidate) => candidate.id === taskId);

            if (task) task.status = 'queued';
          },
        ));
        const detailPatch = dispatch(tasksApi.util.updateQueryData(
          'getTask',
          { projectId, taskId },
          (draft) => { draft.status = 'queued'; },
        ));

        try {
          await queryFulfilled;
        } catch {
          listPatch.undo();
          detailPatch.undo();
        }
      },
    }),
    reorderTask: builder.mutation<Task, ReorderTaskArgs>({
      query: ({ projectId, taskId, position, status }) => ({
        url: `/api/v1/projects/${projectId}/tasks/${taskId}/reorder`,
        method: 'PUT',
        body: { position, status },
      }),
      async onQueryStarted(
        { projectId, taskId, position, status },
        { dispatch, queryFulfilled },
      ) {
        const patch = dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
          const task = draft.find((candidate) => candidate.id === taskId);

          if (task) {
            task.position = position;
            if (status) task.status = status;
          }
        }));

        try {
          const { data } = await queryFulfilled;

          dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
            replaceTask(draft, data);
          }));
          dispatch(tasksApi.util.upsertQueryData('getTask', { projectId, taskId }, data));
        } catch {
          patch.undo();
        }
      },
    }),
    deleteTask: builder.mutation<{ message: string }, TaskIdentity>({
      query: ({ projectId, taskId }) => ({
        url: `/api/v1/projects/${projectId}/tasks/${taskId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ projectId, taskId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
          const next = draft.filter((task) => task.id !== taskId);

          replaceTasks(draft, next);
        }));

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    batchUpdateTaskStatus: builder.mutation<BatchStatusResult, BatchStatusArgs>({
      query: ({ projectId, taskIds, status }) => ({
        url: `/api/v1/projects/${projectId}/tasks/batch-status-update`,
        method: 'PUT',
        body: { task_ids: taskIds, status },
      }),
      async onQueryStarted({ projectId, taskIds, status }, { dispatch, queryFulfilled }) {
        const ids = new Set(taskIds);
        const previous = new Map<string, TaskStatus>();
        const patch = dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
          draft.forEach((task) => {
            if (!ids.has(task.id)) return;
            previous.set(task.id, task.status);
            task.status = status;
          });
        }));

        try {
          const { data } = await queryFulfilled;
          // The server left these as they were (a task the AI model declined is not retried unchanged).
          const skipped = new Set(data?.skipped_task_ids ?? []);

          if (skipped.size === 0) return;
          dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
            draft.forEach((task) => {
              const before = previous.get(task.id);

              if (skipped.has(task.id) && before) task.status = before;
            });
          }));
        } catch {
          patch.undo();
        }
      },
    }),
    batchReorderTasks: builder.mutation<{ message: string }, BatchReorderArgs>({
      query: ({ projectId, updates }) => ({
        url: `/api/v1/projects/${projectId}/tasks/batch-reorder`,
        method: 'PUT',
        body: { updates },
      }),
      async onQueryStarted({ projectId, updates }, { dispatch, queryFulfilled }) {
        const positions = new Map(updates.map((update) => [update.id, update.position]));
        const patch = dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
          draft.forEach((task) => {
            const position = positions.get(task.id);

            if (position !== undefined) task.position = position;
          });
        }));

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    batchDeleteTasks: builder.mutation<{ message: string }, BatchDeleteArgs>({
      query: ({ projectId, taskIds }) => ({
        url: `/api/v1/projects/${projectId}/tasks/batch-delete`,
        method: 'POST',
        body: { task_ids: taskIds },
      }),
      async onQueryStarted({ projectId, taskIds }, { dispatch, queryFulfilled }) {
        const ids = new Set(taskIds);
        const patch = dispatch(tasksApi.util.updateQueryData('getTasks', projectId, (draft) => {
          replaceTasks(draft, draft.filter((task) => !ids.has(task.id)));
        }));

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    getTaskVersions: builder.query<TaskVersion[], string>({
      query: (projectId) => `/api/v1/projects/${projectId}/versions/tasks`,
      keepUnusedDataFor: 60,
      providesTags: (_result, _error, projectId) => [
        { type: 'TaskVersion' as const, id: projectId },
      ],
    }),
    getTaskVersion: builder.query<TaskVersionDetail, TaskVersionIdentity>({
      query: ({ projectId, versionNumber }) => (
        `/api/v1/projects/${projectId}/versions/tasks/${versionNumber}`
      ),
    }),
  }),
});

export const {
  useBatchDeleteTasksMutation,
  useBatchReorderTasksMutation,
  useBatchUpdateTaskStatusMutation,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useGenerateTasksWithAiMutation,
  useGetTaskQuery,
  useGetTasksQuery,
  useGetTaskVersionsQuery,
  useLazyGetTaskQuery,
  useLazyGetTasksQuery,
  useLazyGetTaskVersionQuery,
  usePauseTaskMutation,
  useReorderTaskMutation,
  useResumeTaskMutation,
  useUpdateTaskMutation,
} = tasksApi;
